import { z } from 'zod';
import { CRM_FUNIL_PARCERIA_PROSPECCAO, type CrmFunilTipo } from '@/constants/crmFunil';
import {
  PRODUTO_LEAD_PARCERIA_CRM,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
  PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
  PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
  PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
  PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
  PRODUTO_LEAD_SEM_PRODUTO,
} from '@/constants/leadsProduto';

export const leadSchema = z.object({
  tipo: z.enum(['fisica', 'juridica']),
  nome: z.string().nullable().optional(),
  cpf: z.string().nullable().optional(),
  razaoSocial: z.string().nullable().optional(),
  cnpj: z.string().nullable().optional(),
  representanteLegal: z.string().nullable().optional(),
  cpfRepresentante: z.string().nullable().optional(),
  email: z.string().nullable().optional().refine((val) => !val || z.string().email().safeParse(val).success, { message: 'Email inválido' }),
  telefone: z.string().min(10, 'Telefone inválido'),
  consumoMensal: z.number().nullable().optional(),
  tipoFixacao: z.enum(['Telhado', 'Solo', 'Carport'] as const).nullable().optional(),
  tipoTelha: z.string().nullable().optional(),
  cep: z.string().nullable().optional(),
  endereco: z.string().nullable().optional(),
  numero: z.string().nullable().optional(),
  cidade: z.string().nullable().optional(),
  estado: z.string().nullable().optional(),
  origem: z.string().min(1, 'Origem obrigatória'),
  origemOutro: z.string().nullable().optional(),
  produto: z.enum([
    'Sistema Solar (on-grid)',
    'Sistema Solar Híbrido (com bateria)',
    'Sistema Solar Zero-Grid',
    'Carregadores Veiculares',
    'Instalação de Wallbox',
    'Instalação de Wallbox - Condomínio',
    'Estudo de Viabilidade - EV',
    PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
    PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
    'Kit Anti-Apagão',
    PRODUTO_LEAD_PARCERIA_CRM,
    PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
    PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
    PRODUTO_LEAD_ADEQUACOES_REFORMAS,
    PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
    PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
    PRODUTO_LEAD_SEM_PRODUTO,
  ], {
    error: (issue) =>
      issue.input === undefined ? 'Produto é obrigatório' : 'Selecione um produto válido',
  }),
  vendedor_id: z.string().nullable().optional(), // Tornado opcional - pode ser vazio para parceiros
  parceiro_id: z.string().nullable().optional(),
  mensagem: z.string().nullable().optional(),
  faturas: z.any().optional(), // Arquivos de faturas (FileList ou File[])
  /** Se false, o lead segue o funil sem criação automática de tarefas pelos templates. */
  seguirProcessoTarefas: z.boolean().optional().default(false),
}).superRefine((data, ctx) => {
  // Validação específica para nome quando tipo é 'fisica'
  if (data.tipo === 'fisica') {
    if (!data.nome || data.nome.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nome é obrigatório',
        path: ['nome'],
      });
    }
  }
  // Validação para pessoa jurídica
  if (data.tipo === 'juridica') {
    if (!data.razaoSocial || data.razaoSocial.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Razão Social é obrigatória',
        path: ['razaoSocial'],
      });
    }
    if (!data.representanteLegal || data.representanteLegal.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Representante Legal é obrigatório',
        path: ['representanteLegal'],
      });
    }
  }
  // Validação para origem "Outro"
  if (data.origem === 'Outro' && (!data.origemOutro || data.origemOutro.trim().length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Especifique a origem se "Outro" for selecionado',
      path: ['origemOutro'],
    });
  }
  // Validação para tipo de telha
  if (data.tipoFixacao === 'Telhado' && (!data.tipoTelha || data.tipoTelha.trim().length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Tipo de telha obrigatório para fixação em Telhado',
      path: ['tipoTelha'],
    });
  }
});

export type LeadForm = z.infer<typeof leadSchema>;

/** Formulário exclusivo do funil CRM de prospecção de parcerias (sem campos de energia/FV). */
export const leadSchemaParceiroCrm = z
  .object({
    tipo: z.enum(['fisica', 'juridica']),
    nome: z.string().nullable().optional(),
    cpf: z.string().nullable().optional(),
    razaoSocial: z.string().nullable().optional(),
    cnpj: z.string().nullable().optional(),
    representanteLegal: z.string().nullable().optional(),
    cpfRepresentante: z.string().nullable().optional(),
    email: z
      .string()
      .nullable()
      .optional()
      .refine((val) => !val || val.trim() === '' || z.string().email().safeParse(val).success, { message: 'Email inválido' }),
    telefone: z.string().min(10, 'Telefone inválido'),
    consumoMensal: z.number().nullable().optional(),
    tipoFixacao: z.enum(['Telhado', 'Solo', 'Carport'] as const).nullable().optional(),
    tipoTelha: z.string().nullable().optional(),
    cep: z.string().nullable().optional(),
    endereco: z.string().nullable().optional(),
    numero: z.string().nullable().optional(),
    cidade: z.string().nullable().optional(),
    estado: z.string().nullable().optional(),
    origem: z.string().min(1, 'Origem obrigatória'),
    origemOutro: z.string().nullable().optional(),
    produto: z.literal(PRODUTO_LEAD_PARCERIA_CRM),
    vendedor_id: z.string().nullable().optional(),
    parceiro_id: z.string().nullable().optional(),
    mensagem: z.string().nullable().optional(),
    faturas: z.any().optional(),
    seguirProcessoTarefas: z.boolean().optional().default(false),
    crmFunil: z.literal(CRM_FUNIL_PARCERIA_PROSPECCAO),
  })
  .superRefine((data, ctx) => {
    if (data.tipo === 'fisica') {
      if (!data.nome || data.nome.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nome é obrigatório', path: ['nome'] });
      }
    }
    if (data.tipo === 'juridica') {
      if (!data.razaoSocial || data.razaoSocial.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Razão Social é obrigatória', path: ['razaoSocial'] });
      }
      if (!data.representanteLegal || data.representanteLegal.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Representante Legal é obrigatório',
          path: ['representanteLegal'],
        });
      }
    }
    if (data.origem === 'Outro' && (!data.origemOutro || data.origemOutro.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Especifique a origem se "Outro" for selecionado',
        path: ['origemOutro'],
      });
    }
  });

export type LeadFormParceiroCrm = z.infer<typeof leadSchemaParceiroCrm>;

export interface ExtendedLead extends LeadForm {
  id?: string;
  status?: string;
  createdAt?: string;
  vendedorNome?: string; // Nome do vendedor obtido via join com profiles
  parceiroNome?: string; // Nome do parceiro obtido via join com profiles
  parceiroCidade?: string; // Cidade do parceiro
  parceiroEstado?: string; // Estado do parceiro
  fatura_urls?: string[] | null;
  fotos_urls?: string[] | null;
  projetos_urls?: string[] | null;
  documentos_urls?: string[] | null;
  contratos_urls?: string[] | null;
  propostas_urls?: string[] | null;
  grupo?: string;
  grupo_tarifario?: string;
  padrao?: string;
  tarifaKwh?: number;
  despesaTotal?: number;
  mensagem?: string;
  etapa?: string;
  /** Espelha `leads.seguir_processo_tarefas` (novos leads: false até ativar na aba Tarefas). */
  seguirProcessoTarefas?: boolean;
  categoria?: string;  // Novo campo adicionado para corrigir o erro
  temperatura?: 'Quente' | 'Morno' | 'Frio' | null; // Classificação de temperatura do lead
  dimensionamentos?: Dimensionamento[]; // Lista de dimensionamentos do lead
  /** Indicador do card: tarefas do funil (ou avulsas sem funil) e/ou próximo contato em `lead_contatos`. */
  tarefaStatus?: 'vermelho' | 'amarelo' | 'verde';
  /** `vendas` (default) ou funil de prospecção de parcerias. */
  crmFunil?: CrmFunilTipo;
}

export interface Dimensionamento {
  id: string;
  lead_id: string;
  nome_dimensionamento: string;
  descricao_dimensionamento?: string;
  grupo_tarifario: string;
  created_at: string;
  updated_at?: string;
  finalizado?: boolean; // Status de finalização do dimensionamento
}
